/**
 * Name label stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nameLabelStickersPack: ToilePack = {
    slug: 'name-label-stickers',
    category: 'sticker',
    name: 'Name label stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'dino', label: 'Dinosaur label', subject: 'a rectangular name label with a small dinosaur at one end and a blank band for the name'},
        {key: 'rainbow', label: 'Rainbow label', subject: 'a rectangular name label with a rainbow at one end and a blank band'},
        {key: 'plain', label: 'Bordered label', subject: 'a rectangular name label with a thin coloured border and a blank band'},
    ],
    styles: [
        {key: 'kiss', label: 'Kiss-cut sheet', style: 'kiss-cut sticker sheet, glossy stickers on a matte backing, offset white borders, cut lines drawn', palette: 'Mixed bright colours on a pale backing sheet', framing: 'Nine to twelve stickers arranged in rows on one sheet with a margin, set on a plain background'},
        {key: 'die', label: 'Die-cut set', style: 'die-cut sticker set, each shape cut to its own outline, white border', palette: 'Bright flat colour', framing: 'The set laid out spaced apart on a plain ground'},
        {key: 'planner', label: 'Planner strip', style: 'planner sticker strip, small tidy icons in one pastel palette, cut lines drawn', palette: 'One soft pastel palette', framing: 'Small stickers in neat rows on a narrow strip with a margin, set on a plain background'},
    ],
    directions: ['a space rocket instead', 'a whole sticker sheet', 'in bold primary colors'],
    directionHint: 'try: a space rocket instead',
    seo: {
        title: 'Name Label Stickers: Dinosaur, Rainbow, Plain',
        metaDescription: 'Nine name label stickers — a dinosaur band, a rainbow band, a plain bordered band — in kiss-cut, die-cut, and planner finishes.',
        h1: 'Name label stickers: dinosaur, rainbow, and plain bands in three finishes',
        tagline: 'Nine name labels, three band designs, three finishes each.',
        intro: 'The label for the lunchbox, three ways: a name band with a dinosaur, a name band with a rainbow, a plain bordered band. The name itself is left blank so you fill it in after printing, in kiss-cut, die-cut, and planner-strip finishes. Ask for a different motif — space, flowers, a favorite animal — and the same three finishes carry it.',
        useCases: [
            'lunchbox and water bottle name labels',
            'labeling school supplies',
            'daycare cubby labels',
            'camp gear name tags',
            'a printable name-label sheet',
        ],
        faq: [
            {q: 'Is the name already printed on the sticker?', a: "No — the name band is left blank so you can add the child's name by hand or with your own text after printing."},
            {q: 'What finish should I pick for a water bottle?', a: 'Die-cut or kiss-cut hold up best on curved, washable surfaces; the planner strip finish suits paper and folders.'},
            {q: 'Can I get a different motif, like space or flowers?', a: 'Yes — the direction field takes any motif, rendered in the same three finishes.'},
            {q: 'Will these survive a dishwasher or washing machine?', a: 'These are print-at-home designs, not waterproof vinyl stock — for wash-safe labels, print on a laminated or waterproof sticker paper.'},
            {q: 'Can I print several sheets for a classroom or daycare?', a: 'Yes, for personal and classroom use; check the license terms before reselling the designs.'},
        ],
    },
}
